import { useCallback, useEffect, useMemo, useState } from 'react'

import { useAllProposalsQuery, ProposalNode } from '@/apollo/graphql'
import { IOption, IPageInfo, Maybe } from '@/types'
import { getFetchMoreStatus } from '@/utils'

interface IUseSelect {
  initial?: Maybe<Partial<ProposalNode>>
}

const pageSize = 15

const initialPageInfo: IPageInfo = {
  hasNextPage: false,
  offset: 0,
  fetchMoreStatus: 'loading',
}

export const useProposalSelect = ({ initial }: IUseSelect) => {
  const [proposals, setProposals] = useState<Partial<ProposalNode>[]>([])
  const [pageInfo, setPageInfo] = useState<IPageInfo>(initialPageInfo)
  const [search, setSearch] = useState<string>('')

  const { loading, networkStatus } = useAllProposalsQuery({
    fetchPolicy: 'network-only',
    variables: {
      offset: pageInfo?.offset || 0,
      first: pageSize,
      text: search,
    },
    onCompleted: res => {
      if (res.allProposals?.edges) {
        const _proposals: Partial<ProposalNode>[] = []
        res.allProposals.edges.forEach(e => {
          // @ts-ignore
          if (e?.node) _proposals.push(e.node)
        })
        setProposals(prev => [...prev, ..._proposals])
        setPageInfo(prev => ({
          ...prev,
          hasNextPage: res.allProposals?.pageInfo.hasNextPage,
        }))
      }
    },
  })

  const proposalOptions: IOption<string>[] = useMemo(() => {
    let exists = false
    const opts = proposals.map(p => {
      if (!exists && initial?.id === p.id) {
        exists = true
      }
      return {
        label: `Proposal - ${p.id}`,
        value: p.id || '',
      }
    })

    if (!exists && initial && !search) {
      opts.unshift({
        label: `Proposal - ${initial.id}`,
        value: initial.id || '',
      })
    }
    return opts
  }, [initial, proposals, search])

  useEffect(() => {
    setPageInfo(prev => ({
      ...prev,
      fetchMoreStatus: getFetchMoreStatus({
        networkStatus,
        hasNextPage: prev?.hasNextPage,
        withForce: true,
      }),
    }))
  }, [networkStatus])

  const onFetchMore = useCallback(() => {
    setPageInfo(prev => ({ ...prev, offset: proposals.length }))
  }, [proposals.length])

  const onSearch = useCallback(
    (text: string) => {
      if (search !== text) {
        setProposals([])
      }
      setSearch(text)
      setPageInfo(initialPageInfo)
    },
    [search],
  )

  return {
    proposalOptions,
    loading,
    pageInfo,
    onFetchMore,
    onSearch,
  }
}
